import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import SearchableSelect from 'shared/components/SearchableSelect';
import { formatEnumLabel } from '../constants/enums';

const FULL_WIDTH_TYPES = new Set(['textarea', 'multiEnum', 'multiLookupSelect', 'lookupSelect', 'enum']);

function emitSelectValue(field, setFieldValue, handleChange, next) {
  if (setFieldValue) {
    setFieldValue(field.name, next);
    return;
  }
  handleChange?.({ target: { name: field.name, value: next } });
}

function fieldSpan(field, columns) {
  if (field.gridSpan) return field.gridSpan;
  if (columns !== 2) return 12;
  if (field.fullWidth || FULL_WIDTH_TYPES.has(field.type)) return 12;
  if (field.type === 'boolean') return 12;
  return 6;
}

function fieldHint(field, error) {
  if (error) return error;
  return field.hint || '';
}

function enumOptionLabel(field, opt) {
  if (field.optionLabels?.[opt]) return field.optionLabels[opt];
  return formatEnumLabel(opt);
}

function renderAboveLabel(field, error) {
  if (field.hideLabel || !field.label) return null;
  return (
    <>
      <Typography variant="body2" fontWeight={600} sx={{ mb: field.hint && !error ? 0.25 : 1 }}>
        {field.label}
        {field.required ? ' *' : ''}
      </Typography>
      {field.hint && !error ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {field.hint}
        </Typography>
      ) : null}
    </>
  );
}

const EntityFormFields = ({
  fields,
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  softwareOptions = [],
  lookupOptions = {},
  isEdit = false,
  columns = 1,
  dense = false,
  /** When set (e.g. 280), fields use fixed width on sm+ and full width on xs. */
  fixedFieldWidth = null,
}) => {
  const renderField = (field) => {
    if (field.hidden) return null;
    if (!isEdit && field.hideOnCreate) return null;
    if (isEdit && field.hideOnEdit) return null;
    if (typeof field.showWhen === 'function' && !field.showWhen(values)) return null;

    const error = touched[field.name] && errors[field.name];
    const helper = fieldHint(field, error);
    const inputSize =
      field.inputSize
      ?? (field.type === 'enum' || field.type === 'multiEnum' || field.type?.endsWith('Select')
        ? 'medium'
        : dense
          ? 'small'
          : 'medium');
    const selectSx = field.selectSx;
    const fieldDisabled = Boolean(
      field.disabled
      || (field.disabledOnCreate && !isEdit && values[field.name])
      || (field.disabledOnEdit && isEdit)
    );
    const common = {
      name: field.name,
      onBlur: handleBlur,
      error: Boolean(error),
      helperText: helper || undefined,
      size: inputSize,
      placeholder: field.placeholder,
      disabled: fieldDisabled,
    };

    if (field.type === 'boolean') {
      return (
        <FormControlLabel
          key={field.name}
          sx={{ alignItems: 'flex-start', m: 0 }}
          control={
            <Checkbox
              size={dense ? 'small' : 'medium'}
              checked={Boolean(values[field.name])}
              onChange={(e) => setFieldValue(field.name, e.target.checked)}
              sx={{ pt: 0.25 }}
            />
          }
          label={
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {field.label}
              </Typography>
              {field.hint && !error ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  {field.hint}
                </Typography>
              ) : null}
              {error ? (
                <Typography variant="caption" color="error" display="block">
                  {error}
                </Typography>
              ) : null}
            </Box>
          }
        />
      );
    }

    if (field.type === 'enum') {
      const select = (
        <SearchableSelect
          name={field.name}
          label={field.label}
          hideLabel={Boolean(field.labelAbove)}
          value={values[field.name] ?? ''}
          options={(field.options ?? []).map((opt) => ({
            value: opt,
            label: enumOptionLabel(field, opt),
          }))}
          onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
          onBlur={handleBlur}
          error={Boolean(error)}
          helperText={helper}
          size={inputSize}
          disabled={fieldDisabled}
          required={field.required}
          placeholder={field.placeholder || 'Select…'}
          sx={selectSx}
        />
      );
      if (field.labelAbove) {
        return (
          <Box key={field.name} sx={field.sx}>
            {renderAboveLabel(field, error)}
            {select}
          </Box>
        );
      }
      return (
        <Box key={field.name} sx={{ width: '100%', ...field.sx }}>
          {select}
        </Box>
      );
    }

    if (field.type === 'multiEnum') {
      const select = (
        <SearchableSelect
          multiple
          name={field.name}
          label={field.label}
          hideLabel={Boolean(field.labelAbove)}
          value={values[field.name] ?? []}
          options={(field.options ?? []).map((opt) => ({
            value: opt,
            label: formatEnumLabel(opt),
          }))}
          onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
          onBlur={handleBlur}
          error={Boolean(error)}
          helperText={helper}
          size={inputSize}
          disabled={fieldDisabled}
          required={field.required}
          placeholder={field.placeholder || 'Select…'}
          sx={selectSx}
        />
      );
      if (field.labelAbove) {
        return (
          <Box key={field.name} sx={field.sx}>
            {renderAboveLabel(field, error)}
            {select}
          </Box>
        );
      }
      return (
        <Box key={field.name} sx={{ width: '100%' }}>
          {select}
        </Box>
      );
    }

    if (field.type === 'softwareSelect') {
      return (
        <Box key={field.name} sx={{ width: '100%' }}>
          <SearchableSelect
            name={field.name}
            label={field.label}
            value={values[field.name] ?? ''}
            options={softwareOptions.map((opt) => ({ value: opt.id, label: opt.label }))}
            onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
            onBlur={handleBlur}
            error={Boolean(error)}
            helperText={helper}
            size={inputSize}
            disabled={fieldDisabled}
            required={field.required}
            sx={selectSx}
          />
        </Box>
      );
    }

    if (field.type === 'businessUnitSelect') {
      const options = lookupOptions.businessUnits ?? [];
      return (
        <Box key={field.name} sx={{ width: '100%' }}>
          <SearchableSelect
            name={field.name}
            label={field.label}
            value={values[field.name] ?? ''}
            options={options.map((opt) => ({ value: opt.id, label: opt.name }))}
            onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
            error={Boolean(error)}
            helperText={helper}
            size={inputSize}
            disabled={fieldDisabled}
            required={field.required}
            sx={selectSx}
          />
        </Box>
      );
    }

    if (field.type === 'systemOwnerSelect') {
      const options = lookupOptions.systemOwners ?? [];
      return (
        <Box key={field.name} sx={{ width: '100%' }}>
          <SearchableSelect
            name={field.name}
            label={field.label}
            value={values[field.name] ?? ''}
            options={options.map((opt) => ({ value: opt.id, label: opt.fullName }))}
            onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
            error={Boolean(error)}
            helperText={helper}
            size={inputSize}
            disabled={fieldDisabled}
            required={field.required}
            sx={selectSx}
          />
        </Box>
      );
    }

    if (field.type === 'vendorSelect') {
      const options = lookupOptions.vendors ?? [];
      return (
        <Box key={field.name} sx={{ width: '100%' }}>
          <SearchableSelect
            name={field.name}
            label={field.label}
            value={values[field.name] ?? ''}
            options={options.map((opt) => ({ value: opt.id, label: opt.vendorName }))}
            onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
            error={Boolean(error)}
            helperText={helper}
            size={inputSize}
            disabled={fieldDisabled}
            required={field.required}
            sx={selectSx}
          />
        </Box>
      );
    }

    if (field.type === 'lookupSelect') {
      const options = lookupOptions[field.lookupKey] ?? [];
      const labelKey = field.optionLabel ?? 'name';
      return (
        <Box key={field.name} sx={{ width: '100%' }}>
          <SearchableSelect
            name={field.name}
            label={field.label}
            value={values[field.name] != null ? String(values[field.name]) : ''}
            options={options.map((opt) => ({ value: String(opt.id), label: opt[labelKey] }))}
            onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
            onBlur={handleBlur}
            error={Boolean(error)}
            helperText={helper}
            size={inputSize}
            disabled={fieldDisabled}
            required={field.required}
            sx={selectSx}
          />
        </Box>
      );
    }

    if (field.type === 'multiLookupSelect') {
      const options = lookupOptions[field.lookupKey] ?? [];
      const labelKey = field.optionLabel ?? 'name';
      return (
        <Box key={field.name} sx={{ width: '100%' }}>
          <SearchableSelect
            multiple
            name={field.name}
            label={field.label}
            value={(values[field.name] ?? []).map(String)}
            options={options.map((opt) => ({ value: String(opt.id), label: opt[labelKey] }))}
            onChange={(next) => emitSelectValue(field, setFieldValue, handleChange, next)}
            error={Boolean(error)}
            helperText={helper}
            size={inputSize}
            disabled={fieldDisabled}
            required={field.required}
            sx={selectSx}
          />
        </Box>
      );
    }

    if (field.type === 'textarea') {
      if (field.labelAbove) {
        return (
          <Box key={field.name} sx={field.sx}>
            {renderAboveLabel(field, error)}
            <TextField
              {...common}
              fullWidth
              multiline
              minRows={field.minRows ?? (dense ? 2 : 3)}
              placeholder={field.placeholder}
              value={values[field.name] ?? ''}
              onChange={handleChange}
            />
          </Box>
        );
      }

      return (
        <TextField
          {...common}
          key={field.name}
          fullWidth
          multiline
          minRows={dense ? 2 : 3}
          label={field.label}
          value={values[field.name] ?? ''}
          onChange={handleChange}
        />
      );
    }

    if (field.type === 'date') {
      return (
        <TextField
          {...common}
          key={field.name}
          fullWidth
          type="date"
          label={field.label}
          InputLabelProps={{ shrink: true }}
          value={values[field.name] ?? ''}
          onChange={handleChange}
          sx={field.sx}
        />
      );
    }

    if (field.type === 'number') {
      return (
        <TextField
          {...common}
          key={field.name}
          fullWidth
          type="number"
          label={field.label}
          inputProps={{ min: field.min ?? 0, step: field.step ?? 1 }}
          value={values[field.name] ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (setFieldValue) {
              setFieldValue(field.name, raw === '' ? '' : Number(raw));
            } else {
              handleChange(e);
            }
          }}
          sx={field.sx}
        />
      );
    }

    if (field.labelAbove) {
      return (
        <Box key={field.name} sx={field.sx}>
          {renderAboveLabel(field, error)}
          <TextField
            {...common}
            fullWidth
            placeholder={field.placeholder}
            value={values[field.name] ?? ''}
            onChange={handleChange}
          />
        </Box>
      );
    }

    return (
      <TextField
        {...common}
        key={field.name}
        fullWidth
        label={field.label}
        value={values[field.name] ?? ''}
        onChange={handleChange}
        sx={field.sx}
      />
    );
  };

  if (fixedFieldWidth) {
    return (
      <Box
        display="flex"
        flexWrap="wrap"
        gap={dense ? 1.5 : 2}
        pt={0.5}
        sx={{ width: '100%' }}
      >
        {fields.map((field) => {
          const content = renderField(field);
          if (!content) return null;
          return (
            <Box
              key={field.name}
              sx={{
                width: { xs: '100%', sm: fixedFieldWidth },
                flexShrink: 0,
              }}
            >
              {content}
            </Box>
          );
        })}
      </Box>
    );
  }

  if (columns === 2) {
    return (
      <Grid container spacing={dense ? 1.5 : 2} pt={0.5} sx={{ width: '100%' }}>
        {fields.map((field) => {
          const content = renderField(field);
          if (!content) return null;
          const span = fieldSpan(field, columns);
          return (
            <Grid size={{ xs: 12, sm: span }} key={field.name}>
              {content}
            </Grid>
          );
        })}
      </Grid>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={dense ? 1.25 : 2} pt={0.5}>
      {fields.map((field) => renderField(field))}
    </Box>
  );
};

export { default as FormSection } from './FormSection';

export default EntityFormFields;
